import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { NameCell } from '@openx/components-core/lib/NameCell/NameCell';
import type { DirectDealsListItem } from '@openx/types/directDeals';
import type { Columns } from '@openx/types/tableHelpers';
import { getFormattedAmountValue } from '@openx/utils/lib/getFormattedAmountValue';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AudienceDistributionCell } from '../../DirectDealsList/parts/AudienceDistributionCell';
import { GeoRegionCell } from '../../DirectDealsList/parts/GeoRegionCell';
import { MonthlyValueCell } from '../../DirectDealsList/parts/MonthlyValueCell';
import { PublisherLogo } from '../../DirectDealsList/parts/PublisherLogo';

export type UseDirectDealsListColumns = ({ searchPhrase }: { searchPhrase?: string }) => {
  columns: Columns<DirectDealsListItem>;
};

export const useDirectDealsListColumns: UseDirectDealsListColumns = ({ searchPhrase }) => {
  const { t } = useTranslation();

  const columns: Columns<DirectDealsListItem> = useMemo(
    () => [
      {
        key: 'logo',
        render: ({ logo }) => (
          <Box
            sx={{
              padding: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme => theme.palette.background.boxLight,
              aspectRatio: '16 / 9',
              maxHeight: '90px',
              overflow: 'hidden',
            }}
          >
            <PublisherLogo name={logo} height={90} />
          </Box>
        ),
        title: t('Logo'),
        width: '7%',
      },
      {
        key: 'name',
        render: ({ name, id }) => (
          <NameCell name={name} pathTo={`/direct-deals/${id}/details`} searchPhrase={searchPhrase} />
        ),
        title: t('Name'),
        width: '12%',
        disabled: true,
      },
      {
        key: 'mln_impressions',
        render: ({ mln_impressions }) => <MonthlyValueCell value={mln_impressions} />,
        title: t('Impressions'),
        width: '5%',
      },
      {
        key: 'mln_unique_visitors',
        render: ({ mln_unique_visitors }) => <MonthlyValueCell value={mln_unique_visitors} />,
        title: t('Unique Visitors'),
        width: '5%',
      },
      {
        key: 'audience_distribution',
        render: ({ audience_distribution_female, audience_distribution_male, audience_distribution_other }) => (
          <AudienceDistributionCell
            audience_distribution_female={audience_distribution_female}
            audience_distribution_male={audience_distribution_male}
            audience_distribution_other={audience_distribution_other}
          />
        ),
        title: t('Audience Distribution'),
        width: '6%',
      },
      {
        key: 'cpm',
        render: ({ cpm_low, cpm_high }) => (
          <Typography>
            ${getFormattedAmountValue(cpm_low)} - ${getFormattedAmountValue(cpm_high)}
          </Typography>
        ),
        title: t('Suggested CPM'),
        width: '5%',
      },
      {
        key: 'geo_region',
        render: ({ geo_region }) => <GeoRegionCell value={geo_region} />,
        title: t('Geo Region'),
        width: '10%',
      },
    ],
    [t, searchPhrase]
  );

  return {
    columns,
  };
};
